import { useEffect, useState } from "react";

// Tags are written at runtime so absolute URLs come from the live origin;
// the template cannot know which domain an app will be deployed on.
const MARK = "data-seo";

// The title index.html shipped with, captured before any <Seo> overwrites it,
// so clearing the prop restores it rather than leaving the last page's title.
const fallbackTitle = typeof document === "undefined" ? "" : document.title;

const upsertMeta = (attr, key, content) => {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el?.hasAttribute(MARK)) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(MARK, "");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"][${MARK}]`);
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(MARK, "");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const upsertJsonLd = (key, data) => {
  const el = document.head.querySelector(`script[${MARK}="${key}"]`);
  if (!data) {
    el?.remove();
    return;
  }
  const script = el || document.createElement("script");
  script.setAttribute("type", "application/ld+json");
  script.setAttribute(MARK, key);
  script.textContent = JSON.stringify(data);
  if (!el) document.head.appendChild(script);
};

// Route changes must retrigger the effect: <Seo /> may sit outside the router
// (App.js mounts it above <BrowserRouter>), where navigating re-renders nothing.
// history.pushState/replaceState fire no event, so patch them once, globally.
const routeListeners = new Set();
let historyPatched = false;

const patchHistoryOnce = () => {
  if (historyPatched) return;
  historyPatched = true;
  const notify = () => routeListeners.forEach((fn) => fn());
  const { pushState, replaceState } = window.history;
  window.history.pushState = function (...args) {
    pushState.apply(this, args);
    notify();
  };
  window.history.replaceState = function (...args) {
    replaceState.apply(this, args);
    notify();
  };
  window.addEventListener("popstate", notify);
};

const usePathname = () => {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  useEffect(() => {
    patchHistoryOnce();
    const sync = () => setPathname(window.location.pathname);
    routeListeners.add(sync);
    sync();
    return () => routeListeners.delete(sync);
  }, []);
  return pathname;
};

/**
 * Seo, sets title, canonical, Open Graph, Twitter card and JSON-LD.
 * Renders nothing. Omit a prop and its tag is left out rather than guessed.
 */
export default function Seo({
  title,
  description,
  image,
  type = "website",
  siteName,
  jsonLd,
  publishedTime,
  modifiedTime,
  authorName,
}) {
  const pathname = usePathname();

  useEffect(() => {
    const { origin } = window.location;
    const url = `${origin}${pathname}`;
    const imageUrl = new URL(image || "/og/default.jpg", origin).href;
    const imageAlt = `${siteName || "Electra HR"}, Practical HR support for startups and SMEs`;
    const orgId = `${origin}/#organization`;

    document.title = title || fallbackTitle;

    upsertLink("canonical", url);
    upsertMeta("name", "description", description);

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:site_name", siteName || title);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", imageAlt);
    upsertMeta("property", "og:locale", "en_GB");

    if (type === "article") {
      upsertMeta("property", "article:published_time", publishedTime);
      upsertMeta("property", "article:modified_time", modifiedTime || publishedTime);
      upsertMeta("property", "article:author", authorName);
    }

    // Electra HR uses LinkedIn/Facebook only, no Twitter/X, so no twitter:* tags.
    ["twitter:card", "twitter:title", "twitter:description", "twitter:image"].forEach((k) => upsertMeta("name", k));

    upsertJsonLd("site", {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": orgId,
          name: "Electra HR",
          legalName: "Electra HR Ltd",
          url: origin,
          logo: `${origin}/images/electra-hr-logo.png`,
          image: `${origin}/og/default.jpg`,
          description: "Straight-talking HR consultancy for UK founders and SMEs of 5 to 50 people.",
          email: "hello@electra-hr.com",
          telephone: "+44 1622 957300",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Suite 100, 7 Kenden Business Park, Maritime Close",
            addressLocality: "Rochester",
            addressRegion: "Kent",
            postalCode: "ME2 4JF",
            addressCountry: "GB",
          },
          founder: { "@type": "Person", name: "Vanessa Challess", jobTitle: "Founder & Practising Solicitor" },
          areaServed: { "@type": "Country", name: "United Kingdom" },
        },
        {
          "@type": "WebSite",
          "@id": `${origin}/#website`,
          url: origin,
          name: "Electra HR",
          publisher: { "@id": orgId },
          inLanguage: "en-GB",
        },
      ],
    });
    upsertJsonLd("page", jsonLd);
  }, [pathname, title, description, image, type, siteName, jsonLd, publishedTime, modifiedTime, authorName]);

  return null;
}
